/**
 * Participant page API client (NC-1101, Feat 2.0 #1090).
 *
 * Deliberately NOT the shared apiClient: the participant page is anonymous
 * (no SWA auth cookie required) and must never trigger the 401→AAD-login
 * redirect interceptor. Plain fetch against the same origin; the magic-link
 * credential travels in the x-participant-token header (never the URL/query,
 * so it stays out of server logs).
 */

import { ParticipantInfoPayload } from './types';

const TOKEN_HEADER = 'x-participant-token';
const STORAGE_KEY = 'nc-participant-token';

/** Read `#<runId>.<token>` from the URL fragment, falling back to sessionStorage. */
export function resolveParticipantToken(hash: string): string | null {
  const fromHash = (hash || '').replace(/^#/, '').trim();
  if (fromHash && fromHash.includes('.')) {
    try {
      sessionStorage.setItem(STORAGE_KEY, fromHash);
    } catch {
      // sessionStorage unavailable (private mode quirks) — header still works this visit.
    }
    return fromHash;
  }
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export interface ParticipantProjectFileDownload {
  downloadUrl: string;
  fileName: string;
  sizeBytes: number;
  collectedAt: string;
  validForMinutes: number;
}

async function participantGet<T>(path: string, token: string): Promise<T> {
  const res = await fetch(path, {
    method: 'GET',
    headers: { [TOKEN_HEADER]: token },
  });
  let body: any = null;
  try {
    body = await res.json();
  } catch {
    // non-JSON error page (e.g. SWA 502) — fall through to generic error
  }
  if (!res.ok || !body?.success) {
    const message =
      body?.error ??
      (res.status === 404
        ? 'Unbekannter oder abgelaufener Zugangslink.'
        : `Anfrage fehlgeschlagen (HTTP ${res.status}).`);
    const err = new Error(message) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  return body.data as T;
}

export const participantApi = {
  info(token: string): Promise<ParticipantInfoPayload> {
    return participantGet<ParticipantInfoPayload>('/api/participant/info', token);
  },
  projectFileDownload(token: string): Promise<ParticipantProjectFileDownload> {
    return participantGet<ParticipantProjectFileDownload>('/api/participant/projectfile', token);
  },
};
